/**
 * Update vs Final answer (30 Sep 2026).
 *
 * While helpers run, the agent may write before the real answer ("I've started three
 * agents", "research is back, waiting on the other two"). Julian could not tell which one
 * was the official answer. The backend now stamps each saved reply from the agent's own
 * record of background work it still has out:
 *   meta.phase "update" — written while some of it was still running: a compact muted note
 *   meta.phase "final"  — the first answer once all of it was back: a subtle "Final answer"
 *   no phase            — ordinary chats, unchanged
 * Safety net, never a hidden prompt: if an update is still the last thing in the chat 3
 * minutes after all work stopped, say so and offer one visible tap to ask for the answer.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { create } from "zustand";
import type { SessionStatus, UIMessage } from "../lib/types";
import { helperCountsFromStatus } from "../lib/helperPresentation";
import { useSession } from "../store/session";
import { useStopMarks } from "../lib/stopMarks";
import { foldFor } from "../lib/replyFold";

const NO_FINAL_AFTER_MS = 3 * 60_000;
const ASK_TEXT = "All the helpers are done. Please send me the one final answer now, combining everything they found.";

export interface ReplyPhaseState {
  isUpdate: boolean;
  /** Stamped final AND the chat has settled (never beside a "reply coming" status). */
  isFinal: boolean;
  noFinal: boolean;
  open: boolean;
  toggle: () => void;
  /** An update folded under a later reply of the same request (hidden unless shown). */
  hidden: boolean;
  /** Updates folded under THIS reply (it is the newest of its request). */
  foldedUpdates: number;
  updatesShown: boolean;
  toggleUpdates: () => void;
}

/** Which requests show their folded updates (per tab, keyed by the request's first row). */
const useShownUpdates = create<{ ids: Record<string, true>; toggle: (id: string) => void }>((set) => ({
  ids: {},
  toggle: (id) => set((s) => {
    const ids = { ...s.ids };
    if (ids[id]) delete ids[id]; else ids[id] = true;
    return { ids };
  }),
}));

export function useReplyPhase(message: UIMessage, status: SessionStatus | null | undefined, _isNewestHere: boolean): ReplyPhaseState {
  const phase = (message.meta as { phase?: string } | undefined)?.phase;
  const streaming = !!message.isStreaming;
  const isUpdate = phase === "update" && !streaming;
  const [open, setOpen] = useState(false);
  const counts = helperCountsFromStatus(status);
  const working = status?.busy === true || !!status?.autoTurn || status?.state === "spawning" || status?.state === "killing" ||
    !!(counts && (counts.running || counts.needsYou));
  // The "no final answer" wait counts from when the work STOPPED, never from when the update
  // was written (a 10-minute helper run must not show it the moment the helpers finish).
  const [stoppedWorkingAt, setStoppedWorkingAt] = useState(() => Date.now());
  const wasWorking = useRef(working);
  useEffect(() => {
    if (wasWorking.current && !working) setStoppedWorkingAt(Date.now());
    wasWorking.current = working;
  }, [working]);
  const created = Date.parse(message.createdAt);
  const since = Math.max(Number.isFinite(created) ? created : 0, stoppedWorkingAt);
  const [now, setNow] = useState(() => Date.now());
  const activeId = useSession((s) => s.activeCompanyId);
  const stoppedAt = useStopMarks((s) => (activeId ? s.at[activeId] : undefined));
  // Stopped on purpose after this update: no "no final answer" note (not a failure).
  const stopped = typeof stoppedAt === "number" && Number.isFinite(created) && stoppedAt >= created;
  const messages = useSession((s) => (s.activeCompanyId ? s.messagesByCompany[s.activeCompanyId] : undefined));
  const fold = useMemo(() => (messages ? foldFor(messages, message.id) : foldFor([], message.id)), [messages, message.id]);
  const isAnchor = fold.anchor === message.id;
  const watching = isUpdate && isAnchor && fold.newest && !working && !stopped;
  useEffect(() => {
    if (!watching) return;
    const wait = since + NO_FINAL_AFTER_MS - Date.now();
    if (wait <= 0) { setNow(Date.now()); return; }
    const timer = window.setTimeout(() => setNow(Date.now()), wait + 50);
    return () => window.clearTimeout(timer);
  }, [watching, since]);
  const shownIds = useShownUpdates((s) => s.ids);
  const toggleShown = useShownUpdates((s) => s.toggle);
  const shown = !!(fold.startId && shownIds[fold.startId]);
  const hidden = isUpdate && !isAnchor && !!fold.anchor && !shown;
  // Never "Final answer" while this request is still visibly being worked on (the header would
  // say "reply coming" at the same time): the mark appears once the chat has settled.
  const isFinal = phase === "final" && !streaming && !(fold.current && working);
  return {
    isUpdate, isFinal, noFinal: watching && now - since >= NO_FINAL_AFTER_MS, open, toggle: () => setOpen((v) => !v),
    hidden,
    foldedUpdates: isAnchor ? fold.folded : 0,
    updatesShown: isAnchor && shown,
    toggleUpdates: () => {
      if (!fold.startId) return;
      const opening = !shown;
      toggleShown(fold.startId);
      // Bring the first revealed update into view (it appears above what is being read).
      if (opening && fold.firstUpdateId) {
        const target = fold.firstUpdateId;
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          document.querySelector(`[data-mid="${CSS.escape(target)}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }));
      }
    },
  };
}

/** Under the reply text: "Show more" for a long update, the final mark, or the no-final note. */
export function ReplyPhaseMarks({ phase, clamped }: { phase: ReplyPhaseState; text: string; clamped?: boolean }) {
  const setComposerDraft = useSession((s) => s.setComposerDraft);
  const sendMessage = useSession((s) => s.sendMessage);
  const setToast = useSession((s) => s.setToast);
  function askForAnswer() {
    const state = useSession.getState();
    if (state.composerDraft.trim() || state.attachments.length > 0) {
      setToast("Finish or clear the message you are writing first.", "info");
      return;
    }
    setComposerDraft(ASK_TEXT);
    void sendMessage();
  }
  return (
    <>
      {phase.isUpdate && (clamped || phase.open) && (
        <button type="button" className="msg__updateMore" aria-expanded={phase.open} onClick={phase.toggle}>
          {phase.open ? "Show less" : "Show more"}
        </button>
      )}
      {phase.noFinal && (
        <div className="msg__noFinal" role="status">
          <span>Helpers finished, but no final answer came.</span>
          <button type="button" onClick={askForAnswer}>Get the answer</button>
        </div>
      )}
    </>
  );
}

/** Leads the reply's footer line: "✓ Final answer · 44s · Medium · …" (no extra row). */
export function FinalLead({ phase, more }: { phase: ReplyPhaseState; more: boolean }) {
  if (!phase.isFinal) return null;
  return (
    <>
      <span className="msg__metaFinal" title="Written after every helper and background job was back">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
        Final answer
      </span>
      {more ? " · " : null}
    </>
  );
}

/** Ends the footer line: "· 3 updates" toggles the updates folded under this reply. */
export function UpdatesTail({ phase, lead }: { phase: ReplyPhaseState; lead: boolean }) {
  if (phase.foldedUpdates <= 0) return null;
  return (
    <>
      {lead ? " · " : null}
      <button type="button" className="msg__updatesToggle" aria-expanded={phase.updatesShown} onClick={phase.toggleUpdates}>
        {phase.updatesShown ? "hide updates" : phase.foldedUpdates === 1 ? "1 update" : `${phase.foldedUpdates} updates`}
      </button>
    </>
  );
}
